import { useState } from "react";
import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import { Layout, SEO, Section, Container, BlogCard, BlogCategories } from "@/components";
import { DATOCMS_API_TOKEN } from "@/config";
import { metaTagsFragment, responsiveImageFragment } from "@/modules";
import { Subscription, IBlogPost, IBlogCategory } from "@/types";
import { HorizontalRule } from "@narrative-software/narrative-web-ui";

interface IBlogPage {
  posts: IBlogPost[];
  categories: IBlogCategory[];
  page: {
    id: string;
    title: string;
    slug: string;
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<IBlogPage>;
};

const BlogPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  const [category, setCategory] = useState<string>("");

  if (!data) return null;

  const {
    posts,
    categories,
    page: { seo, title },
  } = data;

  const filteredPosts = category ? posts.filter((post) => post.category?.slug === category) : posts;

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container className="text-center">
          <h1 className="text-h2 font-semibold leading-tight lg:text-h1">{title}</h1>
          <HorizontalRule topMargin bottomMargin />

          <div className="mb-10">
            <BlogCategories items={categories} active={category} handleChange={setCategory} />
          </div>

          {filteredPosts.length ? (
            <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.map((post) => (
                <BlogCard {...post} key={post.id} />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center">There are currently no blog posts in this category.</p>
          )}
        </Container>
      </Section>
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageBlog {
          title
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }

        posts: allPageBlogPosts(first: 100, orderBy: date_DESC) {
          id
          title
          slug
          parentPage {
            slug
          }
          category {
            name
            slug
          }
          image {
            responsiveImage(imgixParams: { auto: format, fit: crop, w: 1200, h: 800 }) {
              ...responsiveImageFragment
            }
          }
        }

        categories: allBlogCategories(orderBy: name_ASC) {
          id
          name
          slug
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IBlogPage>(graphqlRequest);

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default BlogPage;
